import { Matrix, SingularValueDecomposition } from "ml-matrix";

import { debugLog } from "../../../utils/debug";

// Hartley normalization centers points and scales their mean distance to sqrt(2).
function normalizePoints(points) {
  if (!points || points.length === 0) {
    throw new Error("Cannot normalize an empty point set.");
  }

  let centroidX = 0;
  let centroidY = 0;

  for (const point of points) {
    centroidX += point.x;
    centroidY += point.y;
  }

  centroidX /= points.length;
  centroidY /= points.length;

  let meanDistance = 0;

  for (const point of points) {
    const dx = point.x - centroidX;
    const dy = point.y - centroidY;
    meanDistance += Math.sqrt(dx * dx + dy * dy);
  }

  meanDistance /= points.length;

  if (meanDistance < 1e-12) {
    throw new Error("Point normalization failed: degenerate point set.");
  }

  const scale = Math.SQRT2 / meanDistance;

  const transform = new Matrix([
    [scale, 0, -scale * centroidX],
    [0, scale, -scale * centroidY],
    [0, 0, 1],
  ]);

  const normalizedPoints = points.map((point) => ({
    x: scale * (point.x - centroidX),
    y: scale * (point.y - centroidY),
  }));

  return {
    points: normalizedPoints,
    transform,
  };
}

// A valid fundamental matrix has rank 2, so its smallest singular value is zero.
function enforceRankTwo(matrix) {
  const svd = new SingularValueDecomposition(matrix);
  const U = svd.leftSingularVectors;
  const V = svd.rightSingularVectors;
  const singularValues = svd.diagonal.slice();

  singularValues[singularValues.length - 1] = 0;

  const S = Matrix.zeros(3, 3);

  for (let i = 0; i < 3; i += 1) {
    S.set(i, i, singularValues[i]);
  }

  return U.mmul(S).mmul(V.transpose());
}

// Normalized eight-point algorithm for x'^T F x = 0.
export function estimateFundamentalMatrix(correspondences) {
  if (!correspondences || correspondences.length < 8) {
    throw new Error("At least 8 correspondences are required.");
  }

  const pointsA = correspondences.map(
    (correspondence) => correspondence.pointA,
  );

  const pointsB = correspondences.map(
    (correspondence) => correspondence.pointB,
  );

  const normalizedA = normalizePoints(pointsA);
  const normalizedB = normalizePoints(pointsB);
  const rows = [];

  for (let i = 0; i < correspondences.length; i += 1) {
    const pointA = normalizedA.points[i];
    const pointB = normalizedB.points[i];

    const x = pointA.x;
    const y = pointA.y;
    const xp = pointB.x;
    const yp = pointB.y;

    rows.push([xp * x, xp * y, xp, yp * x, yp * y, yp, x, y, 1]);
  }

  const A = new Matrix(rows);

  // The smallest right singular vector solves A f = 0.
  const svdA = new SingularValueDecomposition(A, {
    autoTranspose: true,
  });

  const V = svdA.rightSingularVectors;
  const lastColumn = V.columns - 1;
  const f = [];

  for (let row = 0; row < 9; row += 1) {
    f.push(V.get(row, lastColumn));
  }

  let normalizedF = new Matrix([
    [f[0], f[1], f[2]],
    [f[3], f[4], f[5]],
    [f[6], f[7], f[8]],
  ]);

  normalizedF = enforceRankTwo(normalizedF);

  // Undo Hartley normalization: F = T_B^T F_n T_A.
  let F = normalizedB.transform
    .transpose()
    .mmul(normalizedF)
    .mmul(normalizedA.transform);

  let norm = 0;

  for (let row = 0; row < 3; row += 1) {
    for (let column = 0; column < 3; column += 1) {
      const value = F.get(row, column);
      norm += value * value;
    }
  }

  norm = Math.sqrt(norm);

  if (norm < 1e-12) {
    throw new Error("Fundamental matrix estimation produced a zero matrix.");
  }

  // F is homogeneous, so normalize its scale for consistent numerics.
  F = F.mul(1 / norm);

  return F;
}

export function buildCorrespondences(featuresA, featuresB, matches) {
  if (!featuresA || !featuresB || !matches) {
    return [];
  }

  const correspondences = [];

  for (const match of matches) {
    const pointA = featuresA.keypoints[match.indexA];
    const pointB = featuresB.keypoints[match.indexB];

    if (!pointA || !pointB) {
      continue;
    }

    correspondences.push({
      pointA: {
        x: pointA.x,
        y: pointA.y,
      },
      pointB: {
        x: pointB.x,
        y: pointB.y,
      },
      match,
    });
  }

  return correspondences;
}

// Sampson error approximates geometric reprojection error for the epipolar constraint.
export function sampsonError(F, correspondence) {
  const x = correspondence.pointA.x;
  const y = correspondence.pointA.y;
  const xp = correspondence.pointB.x;
  const yp = correspondence.pointB.y;

  // Epipolar line in image B: l' = F x.
  const fx0 = F.get(0, 0) * x + F.get(0, 1) * y + F.get(0, 2);
  const fx1 = F.get(1, 0) * x + F.get(1, 1) * y + F.get(1, 2);
  const fx2 = F.get(2, 0) * x + F.get(2, 1) * y + F.get(2, 2);

  // Epipolar line in image A: l = F^T x'.
  const ftx0 = F.get(0, 0) * xp + F.get(1, 0) * yp + F.get(2, 0);
  const ftx1 = F.get(0, 1) * xp + F.get(1, 1) * yp + F.get(2, 1);

  const numerator = xp * fx0 + yp * fx1 + fx2;
  const denominator = fx0 * fx0 + fx1 * fx1 + ftx0 * ftx0 + ftx1 * ftx1;

  if (denominator < 1e-12) {
    return Infinity;
  }

  return (numerator * numerator) / denominator;
}

export function filterCorrespondencesByEpipolarGeometry(
  correspondences,
  F,
  threshold = 2.0,
) {
  // Sampson error is squared-distance-like.
  const thresholdSquared = threshold * threshold;

  const accepted = [];
  const rejected = [];

  for (const correspondence of correspondences) {
    const error = sampsonError(F, correspondence);

    const result = {
      ...correspondence,
      sampsonError: error,
    };

    if (Number.isFinite(error) && error <= thresholdSquared) {
      accepted.push(result);
    } else {
      rejected.push(result);
    }
  }

  return {
    accepted,
    rejected,
  };
}

function buildSpatialCells(
  correspondences,
  imageWidth,
  imageHeight,
  columns,
  rows,
) {
  const cells = Array.from(
    {
      length: columns * rows,
    },
    () => [],
  );

  for (const correspondence of correspondences) {
    const point = correspondence.pointA;

    let column = Math.floor((point.x / imageWidth) * columns);
    let row = Math.floor((point.y / imageHeight) * rows);

    column = Math.max(0, Math.min(columns - 1, column));
    row = Math.max(0, Math.min(rows - 1, row));

    cells[row * columns + column].push(correspondence);
  }

  return cells.filter((cell) => cell.length > 0);
}

// Deterministic PRNG keeps RANSAC reproducible for identical inputs.
function createSeededRandom(seed) {
  let state = seed >>> 0;

  return function random() {
    state += 0x6d2b79f5;

    let value = state;

    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);

    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

// Prefer different image regions when building a RANSAC hypothesis.
function spatialRandomSample(
  correspondences,
  count,
  imageWidth,
  imageHeight,
  columns,
  rows,
  random,
) {
  if (correspondences.length < count) {
    throw new Error("Not enough correspondences for the requested sample.");
  }

  const cells = buildSpatialCells(
    correspondences,
    imageWidth,
    imageHeight,
    columns,
    rows,
  );

  const shuffledCells = cells.slice();

  for (let i = shuffledCells.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const temporary = shuffledCells[i];

    shuffledCells[i] = shuffledCells[j];
    shuffledCells[j] = temporary;
  }

  const sample = [];
  const selected = new Set();

  for (const cell of shuffledCells) {
    if (sample.length >= count) {
      break;
    }

    const correspondence = cell[Math.floor(random() * cell.length)];

    sample.push(correspondence);
    selected.add(correspondence);
  }

  while (sample.length < count) {
    const correspondence =
      correspondences[Math.floor(random() * correspondences.length)];

    if (selected.has(correspondence)) {
      continue;
    }

    sample.push(correspondence);
    selected.add(correspondence);
  }

  return sample;
}

// RANSAC estimates F from 8-point hypotheses and scores them with Sampson error.
export function estimateFundamentalRANSAC(
  correspondences,
  {
    iterations = 1500,
    threshold = 1.5,
    imageWidth = null,
    imageHeight = null,
    gridColumns = 4,
    gridRows = 4,
    seed = 0x5f3759df,
  } = {},
) {
  if (!correspondences || correspondences.length < 8) {
    throw new Error("RANSAC requires at least 8 correspondences.");
  }

  const useSpatialSampling =
    Number.isFinite(imageWidth) &&
    Number.isFinite(imageHeight) &&
    imageWidth > 0 &&
    imageHeight > 0;

  let bestF = null;
  let bestInliers = [];
  let bestError = Infinity;

  const thresholdSquared = threshold * threshold;
  const random = createSeededRandom(seed);

  for (let iteration = 0; iteration < iterations; iteration += 1) {
    let candidateF;

    try {
      const sample = useSpatialSampling
        ? spatialRandomSample(
            correspondences,
            8,
            imageWidth,
            imageHeight,
            gridColumns,
            gridRows,
            random,
          )
        : randomSampleFallback(correspondences, 8, random);

      candidateF = estimateFundamentalMatrix(sample);
    } catch {
      continue;
    }

    const inliers = [];
    let totalError = 0;

    for (const correspondence of correspondences) {
      const error = sampsonError(candidateF, correspondence);

      if (error <= thresholdSquared) {
        inliers.push(correspondence);
        totalError += error;
      }
    }

    if (
      inliers.length > bestInliers.length ||
      (inliers.length === bestInliers.length && totalError < bestError)
    ) {
      bestF = candidateF;
      bestInliers = inliers;
      bestError = totalError;
    }
  }

  if (!bestF || bestInliers.length < 8) {
    throw new Error("RANSAC could not estimate a valid fundamental matrix.");
  }

  // Re-estimate F from the complete winning consensus set.
  const refinedF = estimateFundamentalMatrix(bestInliers);

  const refinedInliers = [];
  let refinedError = 0;

  for (const correspondence of correspondences) {
    const error = sampsonError(refinedF, correspondence);

    if (error <= thresholdSquared) {
      refinedInliers.push(correspondence);
      refinedError += error;
    }
  }

  return {
    F: refinedF,
    inliers: refinedInliers,
    outliers: correspondences.filter(
      (correspondence) => !refinedInliers.includes(correspondence),
    ),
    meanError:
      refinedInliers.length > 0
        ? refinedError / refinedInliers.length
        : Infinity,
    iterations,
    threshold,
  };
}

function randomSampleFallback(items, count, random) {
  const selected = new Set();

  while (selected.size < count) {
    selected.add(Math.floor(random() * items.length));
  }

  return Array.from(selected, (index) => items[index]);
}

// Approximate intrinsics assume square pixels, zero skew, and a centered principal point.
export function createCameraMatrix(width, height, focalLengthPixels = null) {
  if (width <= 0 || height <= 0) {
    throw new Error("Image dimensions must be positive.");
  }

  const focalLength = focalLengthPixels ?? Math.max(width, height);
  const cx = width / 2;
  const cy = height / 2;

  return new Matrix([
    [focalLength, 0, cx],
    [0, focalLength, cy],
    [0, 0, 1],
  ]);
}

// A valid essential matrix has singular values (s, s, 0).
function enforceEssentialConstraint(E) {
  const svd = new SingularValueDecomposition(E);

  const U = svd.leftSingularVectors;
  const V = svd.rightSingularVectors;
  const singularValues = svd.diagonal;
  const average = (singularValues[0] + singularValues[1]) / 2;

  debugLog("[Reconstruction] Essential constraint:", {
    rawSingularValues: [...singularValues],
    enforcedSingularValues: [average, average, 0],
    firstSecondRatio:
      singularValues[1] > 1e-12
        ? singularValues[0] / singularValues[1]
        : Infinity,
    thirdRelativeToSecond:
      singularValues[1] > 1e-12
        ? singularValues[2] / singularValues[1]
        : Infinity,
  });

  const S = new Matrix([
    [average, 0, 0],
    [0, average, 0],
    [0, 0, 0],
  ]);

  return U.mmul(S).mmul(V.transpose());
}

// Convert pixel-space epipolar geometry with E = K_B^T F K_A.
export function fundamentalToEssential(
  F,
  cameraMatrixA,
  cameraMatrixB,
  enforceConstraint = true,
) {
  if (!F || !cameraMatrixA || !cameraMatrixB) {
    throw new Error("F, K_A, and K_B are required.");
  }

  const rawE = cameraMatrixB.transpose().mmul(F).mmul(cameraMatrixA);

  if (!enforceConstraint) {
    return rawE;
  }

  return enforceEssentialConstraint(rawE);
}

function invertCameraMatrix(cameraMatrix) {
  const fx = cameraMatrix.get(0, 0);
  const fy = cameraMatrix.get(1, 1);
  const cx = cameraMatrix.get(0, 2);
  const cy = cameraMatrix.get(1, 2);

  if (Math.abs(fx) < 1e-12 || Math.abs(fy) < 1e-12) {
    throw new Error("Camera matrix has an invalid focal length.");
  }

  return new Matrix([
    [1 / fx, 0, -cx / fx],
    [0, 1 / fy, -cy / fy],
    [0, 0, 1],
  ]);
}

export function poseToFundamental(
  rotation,
  translation,
  cameraMatrixA,
  cameraMatrixB,
) {
  const [tx, ty, tz] = translation;

  // E = [t]x R.
  const skewTranslation = new Matrix([
    [0, -tz, ty],
    [tz, 0, -tx],
    [-ty, tx, 0],
  ]);

  const essentialMatrix = skewTranslation.mmul(rotation);

  const inverseA = invertCameraMatrix(cameraMatrixA);
  const inverseB = invertCameraMatrix(cameraMatrixB);

  return inverseB.transpose().mmul(essentialMatrix).mmul(inverseA);
}

function determinant3x3(matrix) {
  const a = matrix.get(0, 0);
  const b = matrix.get(0, 1);
  const c = matrix.get(0, 2);

  const d = matrix.get(1, 0);
  const e = matrix.get(1, 1);
  const f = matrix.get(1, 2);

  const g = matrix.get(2, 0);
  const h = matrix.get(2, 1);
  const i = matrix.get(2, 2);

  return a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
}

// E decomposes into four possible relative poses: (R1, ±t) and (R2, ±t).
export function decomposeEssentialMatrix(E) {
  if (!E) {
    throw new Error("Essential matrix is required.");
  }

  const svd = new SingularValueDecomposition(E);

  const U = svd.leftSingularVectors;
  const V = svd.rightSingularVectors;

  const W = new Matrix([
    [0, -1, 0],
    [1, 0, 0],
    [0, 0, 1],
  ]);

  let rotation1 = U.mmul(W).mmul(V.transpose());
  let rotation2 = U.mmul(W.transpose()).mmul(V.transpose());

  // Proper rotations must have determinant +1.
  if (determinant3x3(rotation1) < 0) {
    rotation1 = rotation1.mul(-1);
  }

  if (determinant3x3(rotation2) < 0) {
    rotation2 = rotation2.mul(-1);
  }

  let tx = U.get(0, 2);
  let ty = U.get(1, 2);
  let tz = U.get(2, 2);

  const translationLength = Math.sqrt(tx * tx + ty * ty + tz * tz);

  if (translationLength < 1e-12) {
    throw new Error("Essential matrix produced a zero translation direction.");
  }

  tx /= translationLength;
  ty /= translationLength;
  tz /= translationLength;

  const translation = [tx, ty, tz];
  const negativeTranslation = [-tx, -ty, -tz];

  const candidates = [
    {
      R: rotation1,
      t: translation,
    },
    {
      R: rotation1,
      t: negativeTranslation,
    },
    {
      R: rotation2,
      t: translation,
    },
    {
      R: rotation2,
      t: negativeTranslation,
    },
  ];

  debugLog(
    "[Reconstruction] Essential decomposition consistency:",
    candidates.map((candidate) => {
      const reconstructedE = essentialFromPose(candidate.R, candidate.t);

      return normalizedMatrixDifference(E, reconstructedE);
    }),
  );

  return candidates;
}

function essentialFromPose(rotation, translation) {
  const [tx, ty, tz] = translation;

  const skewTranslation = new Matrix([
    [0, -tz, ty],
    [tz, 0, -tx],
    [-ty, tx, 0],
  ]);

  return skewTranslation.mmul(rotation);
}

function normalizedMatrixDifference(A, B) {
  let dotAB = 0;
  let dotBB = 0;

  for (let row = 0; row < 3; row += 1) {
    for (let col = 0; col < 3; col += 1) {
      dotAB += A.get(row, col) * B.get(row, col);
      dotBB += B.get(row, col) * B.get(row, col);
    }
  }

  if (dotBB < 1e-12) {
    return Infinity;
  }

  const scale = dotAB / dotBB;

  let squaredError = 0;
  let squaredReference = 0;

  for (let row = 0; row < 3; row += 1) {
    for (let col = 0; col < 3; col += 1) {
      const reference = A.get(row, col);
      const reconstructed = scale * B.get(row, col);
      const difference = reference - reconstructed;

      squaredError += difference * difference;
      squaredReference += reference * reference;
    }
  }

  if (squaredReference < 1e-12) {
    return Infinity;
  }

  return Math.sqrt(squaredError / squaredReference);
}

// Convert pixels to normalized camera coordinates with x_n = K^-1 x.
function normalizeCameraPoint(point, cameraMatrix) {
  const fx = cameraMatrix.get(0, 0);
  const fy = cameraMatrix.get(1, 1);
  const cx = cameraMatrix.get(0, 2);
  const cy = cameraMatrix.get(1, 2);

  if (Math.abs(fx) < 1e-12 || Math.abs(fy) < 1e-12) {
    throw new Error("Camera matrix has an invalid focal length.");
  }

  return {
    x: (point.x - cx) / fx,
    y: (point.y - cy) / fy,
  };
}

// Linear DLT triangulation using P_A = [I|0] and P_B = [R|t].
export function triangulatePoint(
  pointA,
  pointB,
  cameraMatrixA,
  cameraMatrixB,
  rotation,
  translation,
) {
  const normalizedA = normalizeCameraPoint(pointA, cameraMatrixA);
  const normalizedB = normalizeCameraPoint(pointB, cameraMatrixB);

  const xA = normalizedA.x;
  const yA = normalizedA.y;
  const xB = normalizedB.x;
  const yB = normalizedB.y;

  const projectionA = new Matrix([
    [1, 0, 0, 0],
    [0, 1, 0, 0],
    [0, 0, 1, 0],
  ]);

  const projectionB = new Matrix([
    [
      rotation.get(0, 0),
      rotation.get(0, 1),
      rotation.get(0, 2),
      translation[0],
    ],
    [
      rotation.get(1, 0),
      rotation.get(1, 1),
      rotation.get(1, 2),
      translation[1],
    ],
    [
      rotation.get(2, 0),
      rotation.get(2, 1),
      rotation.get(2, 2),
      translation[2],
    ],
  ]);

  // x × PX = 0 gives two independent equations per camera.
  const A = new Matrix([
    [
      xA * projectionA.get(2, 0) - projectionA.get(0, 0),
      xA * projectionA.get(2, 1) - projectionA.get(0, 1),
      xA * projectionA.get(2, 2) - projectionA.get(0, 2),
      xA * projectionA.get(2, 3) - projectionA.get(0, 3),
    ],
    [
      yA * projectionA.get(2, 0) - projectionA.get(1, 0),
      yA * projectionA.get(2, 1) - projectionA.get(1, 1),
      yA * projectionA.get(2, 2) - projectionA.get(1, 2),
      yA * projectionA.get(2, 3) - projectionA.get(1, 3),
    ],
    [
      xB * projectionB.get(2, 0) - projectionB.get(0, 0),
      xB * projectionB.get(2, 1) - projectionB.get(0, 1),
      xB * projectionB.get(2, 2) - projectionB.get(0, 2),
      xB * projectionB.get(2, 3) - projectionB.get(0, 3),
    ],
    [
      yB * projectionB.get(2, 0) - projectionB.get(1, 0),
      yB * projectionB.get(2, 1) - projectionB.get(1, 1),
      yB * projectionB.get(2, 2) - projectionB.get(1, 2),
      yB * projectionB.get(2, 3) - projectionB.get(1, 3),
    ],
  ]);

  // Solve A X = 0 with the smallest right singular vector.
  const svd = new SingularValueDecomposition(A);
  const V = svd.rightSingularVectors;
  const lastColumn = V.columns - 1;

  const homogeneous = [
    V.get(0, lastColumn),
    V.get(1, lastColumn),
    V.get(2, lastColumn),
    V.get(3, lastColumn),
  ];

  const w = homogeneous[3];

  if (Math.abs(w) < 1e-12) {
    return null;
  }

  return {
    x: homogeneous[0] / w,
    y: homogeneous[1] / w,
    z: homogeneous[2] / w,
  };
}

export function triangulateCorrespondences(
  correspondences,
  cameraMatrixA,
  cameraMatrixB,
  rotation,
  translation,
) {
  const accepted = [];
  const rejected = [];

  for (const correspondence of correspondences) {
    const point = triangulatePoint(
      correspondence.pointA,
      correspondence.pointB,
      cameraMatrixA,
      cameraMatrixB,
      rotation,
      translation,
    );

    if (!point) {
      rejected.push({
        correspondence,
        reason: "triangulation-failed",
      });

      continue;
    }

    const depthA = point.z;
    const depthB = depthInCameraB(point, rotation, translation);

    const result = {
      ...point,
      depthA,
      depthB,
      correspondence,
    };

    // Cheirality requires the point to lie in front of both cameras.
    if (
      Number.isFinite(point.x) &&
      Number.isFinite(point.y) &&
      Number.isFinite(point.z) &&
      depthA > 0 &&
      depthB > 0
    ) {
      accepted.push(result);
    } else {
      rejected.push({
        ...result,
        reason: "cheirality",
      });
    }
  }

  return {
    accepted,
    rejected,
  };
}

function projectPointToCameraA(point, cameraMatrix) {
  if (point.z <= 1e-12) {
    return null;
  }

  const fx = cameraMatrix.get(0, 0);
  const fy = cameraMatrix.get(1, 1);
  const cx = cameraMatrix.get(0, 2);
  const cy = cameraMatrix.get(1, 2);

  return {
    x: fx * (point.x / point.z) + cx,
    y: fy * (point.y / point.z) + cy,
  };
}

function projectPointToCameraB(point, cameraMatrix, rotation, translation) {
  const x =
    rotation.get(0, 0) * point.x +
    rotation.get(0, 1) * point.y +
    rotation.get(0, 2) * point.z +
    translation[0];

  const y =
    rotation.get(1, 0) * point.x +
    rotation.get(1, 1) * point.y +
    rotation.get(1, 2) * point.z +
    translation[1];

  const z =
    rotation.get(2, 0) * point.x +
    rotation.get(2, 1) * point.y +
    rotation.get(2, 2) * point.z +
    translation[2];

  if (z <= 1e-12) {
    return null;
  }

  const fx = cameraMatrix.get(0, 0);
  const fy = cameraMatrix.get(1, 1);
  const cx = cameraMatrix.get(0, 2);
  const cy = cameraMatrix.get(1, 2);

  return {
    x: fx * (x / z) + cx,
    y: fy * (y / z) + cy,
  };
}

function pointDistance2D(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;

  return Math.sqrt(dx * dx + dy * dy);
}

export function filterTriangulatedByReprojection(
  points,
  cameraMatrixA,
  cameraMatrixB,
  rotation,
  translation,
  maximumError = 3.0,
) {
  const accepted = [];
  const rejected = [];

  for (const point of points) {
    const correspondence = point.correspondence;

    if (!correspondence) {
      rejected.push({
        ...point,
        reason: "missing-correspondence",
      });

      continue;
    }

    const projectedA = projectPointToCameraA(point, cameraMatrixA);

    const projectedB = projectPointToCameraB(
      point,
      cameraMatrixB,
      rotation,
      translation,
    );

    if (!projectedA || !projectedB) {
      rejected.push({
        ...point,
        reason: "projection-failed",
      });

      continue;
    }

    const errorA = pointDistance2D(projectedA, correspondence.pointA);
    const errorB = pointDistance2D(projectedB, correspondence.pointB);

    // Combined reprojection error across both image observations.
    const reprojectionError = Math.sqrt(errorA * errorA + errorB * errorB);

    const result = {
      ...point,
      projectedA,
      projectedB,
      errorA,
      errorB,
      reprojectionError,
    };

    if (
      Number.isFinite(reprojectionError) &&
      reprojectionError <= maximumError
    ) {
      accepted.push(result);
    } else {
      rejected.push({
        ...result,
        reason: "reprojection-error",
      });
    }
  }

  return {
    accepted,
    rejected,
  };
}

// Camera B depth is the z component of X_B = R X + t.
function depthInCameraB(point, rotation, translation) {
  return (
    rotation.get(2, 0) * point.x +
    rotation.get(2, 1) * point.y +
    rotation.get(2, 2) * point.z +
    translation[2]
  );
}

function evaluatePoseCandidate(
  pose,
  correspondences,
  cameraMatrixA,
  cameraMatrixB,
) {
  const points = [];
  let positiveDepthCount = 0;

  for (const correspondence of correspondences) {
    const point = triangulatePoint(
      correspondence.pointA,
      correspondence.pointB,
      cameraMatrixA,
      cameraMatrixB,
      pose.R,
      pose.t,
    );

    if (!point) {
      continue;
    }

    const depthA = point.z;
    const depthB = depthInCameraB(point, pose.R, pose.t);
    const inFront = depthA > 0 && depthB > 0;

    if (inFront) {
      positiveDepthCount += 1;
    }

    points.push({
      ...point,
      depthA,
      depthB,
      inFront,
      correspondence,
    });
  }

  return {
    R: pose.R,
    t: pose.t,
    points,
    positiveDepthCount,
  };
}

// Cheirality resolves the four-way essential decomposition ambiguity.
export function recoverCameraPose(
  E,
  correspondences,
  cameraMatrixA,
  cameraMatrixB,
) {
  if (!correspondences || correspondences.length === 0) {
    throw new Error("Pose recovery requires correspondences.");
  }

  const candidates = decomposeEssentialMatrix(E);

  const evaluated = candidates.map((pose) =>
    evaluatePoseCandidate(pose, correspondences, cameraMatrixA, cameraMatrixB),
  );

  evaluated.sort(
    (candidateA, candidateB) =>
      candidateB.positiveDepthCount - candidateA.positiveDepthCount,
  );

  const best = evaluated[0];

  if (!best || best.positiveDepthCount === 0) {
    throw new Error("Cheirality test could not identify a valid camera pose.");
  }

  return {
    R: best.R,
    t: best.t,
    points: best.points.filter((point) => point.inFront),
    positiveDepthCount: best.positiveDepthCount,
    candidateCounts: evaluated.map((candidate) => candidate.positiveDepthCount),
  };
}

export function essentialToFundamental(E, cameraMatrixA, cameraMatrixB) {
  const inverseA = invertCameraMatrix(cameraMatrixA);
  const inverseB = invertCameraMatrix(cameraMatrixB);

  return inverseB.transpose().mmul(E).mmul(inverseA);
}
